import React from 'react';
import clsx from 'clsx';
import Autocomplete, { AutocompleteProps } from '@material-ui/lab/Autocomplete';

import './CountryStateFilterSearchPanel.css';
import { COUNTRY_FILTER_TREE_NODE_ID } from 'constants/filter';
import { noop } from 'util/function';
import { TransformedFilterTreeNode } from 'components/tree/FilterTree';
import FilterSearchPanel, { FilterSearchPanelProps } from 'components/panel/filter/FilterSearchPanel';
import TextField from 'components/input/TextField';

//
// --- Types ---

export interface CountryStateFilterSearchPanelProps extends FilterSearchPanelProps {}

// --- Helper Components ---

interface CountryAutoCompleteProps
  extends Omit<
    AutocompleteProps<TransformedFilterTreeNode, false, false, false>,
    'getOptionLabel' | 'options' | 'renderInput'
  > {
  nodeData?: TransformedFilterTreeNode;
}

const CountryAutoComplete: React.FC<CountryAutoCompleteProps> = props => {
  const { autoHighlight = true, classes = {}, nodeData, ...remainingAutoCompleteProps } = props;

  if (!nodeData) {
    return null;
  }

  return (
    <Autocomplete
      autoHighlight={autoHighlight}
      classes={{ ...classes, listbox: clsx('country-state-filter-listbox', classes?.listbox) }}
      getOptionLabel={(option: TransformedFilterTreeNode) => (option.name ? option.name : '')}
      options={[...nodeData.children]}
      renderInput={params => (
        <TextField
          {...params}
          className="country-state-filter-search-panel-other-textfield"
          placeholder="Choose a country"
          size="small"
        />
      )}
      {...remainingAutoCompleteProps}
    />
  );
};

//
// --- CountryStateFilterSearchPanel Component ---

const CountryStateFilterSearchPanel: React.FC<CountryStateFilterSearchPanelProps> = props => {
  const {
    className,
    'data-testid': dataTestId = 'country-state-filter-search-panel',
    filterTreeData = [],
    onFilterCheckedChange = noop,
    ...remainingFilterSearchPanelProps
  } = props;

  const updatedCountryFilterTreeData =
    Array.isArray(filterTreeData) && filterTreeData.length > 0
      ? [
          {
            ...filterTreeData[0],
            children: filterTreeData[0].children.map((treeNode: TransformedFilterTreeNode) =>
              treeNode.id === COUNTRY_FILTER_TREE_NODE_ID.OTHER
                ? {
                    ...treeNode,
                    component: (
                      <CountryAutoComplete
                        aria-label="choose a country"
                        className="country-filter-tree-autocomplete"
                        id="country-filter-tree-autocomplete"
                        onChange={(event, changedTreeNode) => {
                          if (changedTreeNode) {
                            onFilterCheckedChange(changedTreeNode.id, true);
                          }
                        }}
                      />
                    )
                  }
                : treeNode
            )
          }
        ]
      : [];

  return (
    <FilterSearchPanel
      className={clsx('country-state-filter-search-panel', className)}
      data-testid={dataTestId}
      filterTreeData={updatedCountryFilterTreeData}
      onFilterCheckedChange={onFilterCheckedChange}
      {...remainingFilterSearchPanelProps}
    />
  );
};

export default CountryStateFilterSearchPanel;
